import React, { useState, useEffect, useRef, useCallback } from "react";
import { finishedKey } from "../../utils/tourUtils";
import letterAudio from "../../assets/letter.mp3";
import { colors, radius, spacing, elevation, transitions } from "../../utils/designSystem";

// Typewriter component defined outside to prevent remounting on parent re-renders
function Typewriter({ text = "", start = false, speed = 26, skip = false, onDone }) {
  const [count, setCount] = useState(0);
  const done = count >= text.length;

  useEffect(() => {
    if (skip) { setCount(text.length); onDone?.(); return; }
    if (!start || done) return;

    const prev = text[Math.max(0, count - 1)] || "";
    const delay = /[.!?,]/.test(prev) ? speed * 6 : speed;
    const t = setTimeout(() => setCount((c) => Math.min(text.length, c + 1)), delay);
    return () => clearTimeout(t);
  }, [skip, start, done, text, speed, count, onDone]);

  useEffect(() => { if (done) onDone?.(); }, [done, onDone]);

  const shown = text.slice(0, count);
  const parts = shown.split(/\n\n/);
  return (
    <div className="space-y-3" aria-live="polite">
      {parts.map((p, i) => (<p key={i} className="text-slate-800 whitespace-pre-wrap">{p}</p>))}
    </div>
  );
}

export function IntroLetter({ slug, onBegin }) {

  const [opened, setOpened] = useState(false);
  const [typingDone, setTypingDone] = useState(false);
  const [skipTyping, setSkipTyping] = useState(false);

  const [twRun, setTwRun] = useState(0);

  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = new Audio(letterAudio);
    audio.loop = false;
    audioRef.current = audio;
  }, []);

  useEffect(() => {
    if (opened && !typingDone && audioRef.current && !isMuted) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [opened, typingDone, isMuted]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = isMuted;
  }, [isMuted]);

  const onBeginRef = useRef(onBegin);
  useEffect(() => { onBeginRef.current = onBegin; }, [onBegin]);

  useEffect(() => {
    const finished = localStorage.getItem(finishedKey(slug));
    if (finished === "true") onBeginRef.current?.();
    else {

      setOpened(false);
      setTypingDone(false);
      setSkipTyping(false);
      setTwRun((n) => n + 1);
    }
  }, [slug]);

  const handleOpen = () => {

    if (opened) return;
    setOpened(true);
    setTypingDone(false);
    setSkipTyping(false);
    setTwRun((n) => n + 1);
  };

  const handleBegin = () => {
    // Stop audio when leaving the intro letter
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    onBeginRef.current?.();
  };

  // Memoize the typing completion handler to prevent Typewriter from restarting
  const handleTypingDone = useCallback(() => {
    setTypingDone(true);
  }, []);

  const LETTER_TEXT =
    "Dear Explorer,\n\n" +
    "Welcome to San Juan, a city of history, beauty, and hidden stories. I’m a historian who spent years searching for the lost treasure of Roberto Cofresí, Puerto Rico’s most legendary pirate.\n\n" +
    "They say his final treasure was left somewhere in these streets. I found it. Now I want to share that journey with you. Each clue will lead you through Old San Juan, revealing pieces of the past and testing your curiosity.\n\n" +
    "If you solve all twelve riddles, there may even be a piece of treasure waiting for you!";

  return (
    <div className="route-fade relative min-h-screen bg-coastal text-slate-900 font-body px-4 py-10">
      <style>{`
        .bg-coastal {
          background: linear-gradient(135deg, #ff9966 0%, #ff5e62 50%, #fcb045 100%);
          background-attachment: fixed;
        }
        .envelope-scene { position: relative; width: 360px; height: 340px; perspective: 1400px; }
        @media (min-width: 420px){ .envelope-scene { width: 400px; height: 360px; } }
        .envelope { position: absolute; inset: 40px 0 0 0; margin: 0 auto; width: 100%; height: 260px; border-radius: 16px; background: linear-gradient(180deg, #f6e5bd, #ecd5a1); box-shadow: 0 10px 30px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.1); border: 1px solid rgba(0,0,0,0.08); transform-style: preserve-3d; transition: transform 600ms ease; z-index: 1; overflow: hidden; }
        .envelope.opened { overflow: visible; }
        .envelope-body { position: absolute; inset: 0; border-radius: 16px; background: linear-gradient(180deg, #f8e9c6, #f2ddb2); z-index: 1; }
        .envelope-flap { position: absolute; top: 0; left: 0; right: 0; height: 58%; background: linear-gradient(180deg, #f0dbac, #e6cd98); clip-path: polygon(0 0, 100% 0, 50% 100%); transform-origin: top center; transition: transform 1100ms cubic-bezier(.2,.7,.2,1); backface-visibility: hidden; z-index: 3; }
        .envelope.opened .envelope-flap { transform: rotateX(182deg); }
        .wax-seal { position: absolute; top: 54%; left: 50%; transform: translate(-50%, -50%); width: 64px; height: 64px; border-radius: 50%; background: #8b1a1a; color: #fff; display: grid; place-items: center; font-weight: 800; letter-spacing: 1px; box-shadow: inset 0 6px 10px rgba(255,255,255,0.15), inset 0 -10px 16px rgba(0,0,0,0.25), 0 6px 18px rgba(0,0,0,0.25); z-index: 4; transition: opacity 600ms ease; }
        .envelope.opened .wax-seal { opacity: 0; }
        .letter { position: absolute; left: 50%; top: -12px; transform: translate(-50%, 0) scale(0.98); opacity: 0; filter: drop-shadow(0 6px 10px rgba(0,0,0,0.22)); transition: opacity 450ms ease, transform 450ms ease; z-index: 5; will-change: transform, opacity; }
        .envelope.opened .letter { opacity: 1; transform: translate(-50%, 0) scale(1); }
        .letter-card { width: 380px; aspect-ratio: 8.5 / 11; height: auto; border-radius: 2px; padding: 24px 28px; background: #fffef9; box-shadow: 0 18px 46px rgba(0,0,0,0.22); border: 1px solid rgba(0,0,0,0.12); text-align: left; font-family: 'Special Elite', 'Courier New', Courier, monospace; }
        @media (max-width: 420px){ .letter-card { width: 340px; } }
        .route-fade { animation: routeFade 420ms cubic-bezier(.2,.7,.2,1); }
        @keyframes routeFade { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0); } }
        .signature-ar { font-family: 'Special Elite', 'Courier New', Courier, monospace; font-size: 22px; font-weight: 700; color: #111; }
        .fade-in-letter { opacity: 0; animation: fadeIn 420ms ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <div className="mx-auto max-w-md text-center">
        <div className="min-h-[70vh] grid place-items-center">
          {/* Controls above the letter - only show when letter is opened and typing */}
          {opened && !typingDone && (
            <div className="mb-4 flex items-center justify-center gap-3" style={{ minHeight: '32px' }}>
              <button
                onClick={() => setSkipTyping(true)}
                className="text-sm font-medium text-slate-700 hover:text-slate-900"
                style={{
                  background: colors.neutral.white,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: radius.lg,
                  padding: `${spacing[2]} ${spacing[3]}`,
                  transition: transitions.default,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.neutral[50];
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colors.neutral.white;
                }}
                title="Skip typing animation"
                aria-label="Skip typing animation"
              >
                Skip animation
              </button>
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="text-sm font-medium text-slate-700 hover:text-slate-900"
                style={{
                  background: colors.neutral.white,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: radius.lg,
                  padding: `${spacing[2]} ${spacing[3]}`,
                  transition: transitions.default,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.neutral[50];
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colors.neutral.white;
                }}
                title={isMuted ? "Unmute narration" : "Mute narration"}
                aria-label={isMuted ? "Unmute narration" : "Mute narration"}
              >
                {isMuted ? '🔇 Unmute' : '🔊 Mute'}
              </button>
            </div>
          )}
          <div className="envelope-scene">
            {}
            <div
              role="button"
              aria-label="Tap to open letter"
              onClick={handleOpen}
              className={`envelope ${opened ? 'opened' : ''}`}
            >
              <div className="envelope-flap"></div>
              <div className="envelope-body"></div>

              {}
              <div className="letter" aria-live="polite">
                <div className="letter-card">
                  <div className="text-center">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">From the Desk of</div>
                    <div className="mb-2 text-lg font-extrabold">Dr. Arturo Rivera</div>
                  </div>

                  {}
                  {typingDone ? (
                    <div className="space-y-3" aria-live="polite">
                      {LETTER_TEXT.split(/\n\n/).map((p, i) => (
                        <p key={i} className="text-slate-800 whitespace-pre-wrap">{p}</p>
                      ))}
                    </div>
                  ) : (
                    <Typewriter
                      key={`body_${twRun}`}
                      text={LETTER_TEXT}
                      start={opened && !typingDone}
                      speed={64}
                      skip={skipTyping}
                      onDone={handleTypingDone}
                    />
                  )}

                  {}
                  <div className="mt-6 flex justify-center">
                    {typingDone && (
                      <button
                        onClick={handleBegin}
                        className="text-center"
                        style={{
                          background: colors.neutral.black,
                          color: colors.neutral.white,
                          border: 'none',
                          borderRadius: radius.full,
                          padding: `${spacing[3]} ${spacing[6]}`,
                          fontWeight: 600,
                          boxShadow: elevation.sm,
                          transition: transitions.default,
                          width: '85%',
                          maxWidth: '20rem',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = colors.neutral[800];
                          e.currentTarget.style.boxShadow = elevation.md;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = colors.neutral.black;
                          e.currentTarget.style.boxShadow = elevation.sm;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Begin Adventure →
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="wax-seal" aria-hidden="true">AR</div>
              <div className="envelope-seam" />
            </div>

            {!opened && <p className="mt-3 text-slate-600 text-sm">Tap to open the envelope</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
